import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { criticalAlertSx } from "../model/alertStyles";
import AlertDetails from "./AlertDetails";
export default function AlertsTable({
  alerts,
  loading,
  error,
  onRefresh,
  resetKey = "",
  hasFilters = false,
}) {
  const [page, setPage] = useState(0),
    [pageSize, setPageSize] = useState(25),
    [selectedId, setSelectedId] = useState(null);
  useEffect(() => {
    setPage(0);
    setSelectedId(null);
  }, [resetKey]);
  const selected = alerts.find((item) => item.id === selectedId) || null;
  const safePage = Math.min(page, Math.max(0, Math.ceil(alerts.length / pageSize) - 1));
  const visible = alerts.slice(safePage * pageSize, (safePage + 1) * pageSize);
  return (
    <>
      <AlertDetails alert={selected} onClose={() => setSelectedId(null)} />
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        {loading && <LinearProgress aria-label="Cargando alertas" />}
        {error && (
          <Alert severity="error" action={<Button onClick={onRefresh}>Reintentar</Button>}>
            {error}
            {alerts.length ? " Se conservan los últimos datos disponibles." : ""}
          </Alert>
        )}
        <TableContainer sx={{ maxHeight: "65vh", borderRadius: 0, boxShadow: "none" }}>
          <Table
            stickyHeader
            size="small"
            aria-label="Alertas por equipo"
            sx={{
              minWidth: 1100,
              tableLayout: "fixed",
              "& th": {
                bgcolor: "#eef3f8",
                color: "#526579",
                fontWeight: 700,
                fontSize: "0.75rem",
              },
              "& td": { fontSize: "0.8rem", color: "#243b53", py: 1.5, overflowWrap: "anywhere" },
            }}
          >
            <colgroup>
              {[14, 16, 18, 34, 11, 7].map((width, index) => (
                <col key={index} style={{ width: `${width}%` }} />
              ))}
            </colgroup>
            <TableHead sx={{ display: "table-header-group", p: 0 }}>
              <TableRow>
                {[
                  "Fecha · Bogotá",
                  "Comercio",
                  "Equipo / IP",
                  "Problema",
                  "Prioridad",
                  "Detalle",
                ].map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((alert) => (
                <TableRow key={alert.id} hover sx={criticalAlertSx(alert)}>
                  <TableCell>
                    {alert.time}
                    <Typography variant="caption" display="block" sx={{ color: "#526579" }}>
                      Registro #{alert.recordId ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{alert.commerce}</TableCell>
                  <TableCell>
                    {alert.equipment}
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ color: "#526579", mt: 0.5, fontFamily: "monospace" }}
                    >
                      {alert.ip}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        lineHeight: 1.5,
                        fontWeight: 700,
                        color: alert.severity === "error" ? "#b42318" : "#243b53",
                      }}
                    >
                      {alert.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={alert.priorityText}
                      sx={{
                        fontWeight: 600,
                        color:
                          alert.severity === "error"
                            ? "#b42318"
                            : alert.severity === "warning"
                            ? "#925600"
                            : "#245a83",
                        bgcolor:
                          alert.severity === "error"
                            ? "#fff1f0"
                            : alert.severity === "warning"
                            ? "#fff6e6"
                            : "#eaf2ff",
                      }}
                    />
                    <Typography variant="caption" display="block" sx={{ color: "#526579" }}>
                      {alert.priority != null ? `Valor ${alert.priority}` : "Sin valor"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => setSelectedId(alert.id)}
                      aria-label={`Ver detalle de alerta ${alert.recordId ?? alert.id}`}
                    >
                      Ver
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!visible.length && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ p: 4, textAlign: "center" }}>
                      <Typography>
                        {loading
                          ? "Cargando alertas…"
                          : hasFilters
                          ? "No hay alertas que coincidan con los filtros."
                          : "No hay alertas registradas para mostrar."}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={alerts.length}
          page={safePage}
          onPageChange={(_, value) => setPage(value)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Paper>
    </>
  );
}
AlertsTable.propTypes = {
  alerts: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRefresh: PropTypes.func.isRequired,
  resetKey: PropTypes.string,
  hasFilters: PropTypes.bool,
};
